import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, CalendarIcon, MailPlus, ChevronDown, BookmarkPlus, Share2, UserRound, X } from "lucide-react";
import { format } from "date-fns";
import { track } from "@/lib/track";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import PropertyCard from "@/components/PropertyCard";
import { useProperties } from "@/hooks/useProperties";
import { useFavorites } from "@/hooks/useFavorites";
import AuthPromptDialog from "@/components/AuthPromptDialog";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { scoreProperty, hasAnyPreference, type RenterPreferences } from "@/lib/matchScore";
import Seo from "@/components/Seo";
import AlertSignup from "@/components/alerts/AlertSignup";
import { PageContainer } from "@/components/ui/page-container";
import { Chip } from "@/components/ui/chip";
import { useAbsUrl, useTenant } from "@/hooks/useTenant";
import { useCopyResolver } from "@/hooks/useSiteCopy";
import { seoDescriptionKey, seoTitleKey } from "@/lib/siteCopy";
import { useMatchConfig } from "@/hooks/useMatchConfig";
import {
  profileRowToPreferences,
  browseFiltersToProfilePayload,
  pruneDealbreakers,
  preferencesToBrowseParams,
  searchDiffersFromProfile,
  keepRankedTypes,
} from "@/lib/renterPreferences";
import { preferencesForSite, replaceMarketAreas } from "@/lib/renterMarkets";
import {
  BROWSE_FILTER_KEYS,
  BATHROOM_OPTIONS,
  BEDROOM_OPTIONS,
  TYPE_OPTIONS,
  activeFilterChips,
  applyBrowseSearch,
  areaSlugFor,
  browseSearchToParams,
  clearBrowseFilters,
  countActiveFilters,
  filterProperties,
  parseBrowseSearch,
  pickBrowseParams,
  priceBoundsOf,
  resolveSort,
  sortProperties,
  promoteProperties,
  relaxations,
  typeLabel,
  type BathroomOption,
  type BedroomOption,
  type BrowseContext,
  type BrowseSearch,
  type BrowseSort,
} from "@/lib/browseFilters";
import { usePromotions } from "@/lib/monetization";
import { rememberBrowseSearch } from "@/lib/lastBrowseSearch";
import { labelsToKeys } from "@/lib/amenities";
import { formatMoney, tenantCurrency } from "@/lib/money";
import { foldKey } from "@/lib/foldKey";
import { toast } from "sonner";
import { SERVICE_OPTIONS as SERVICE_FILTERS } from "@/lib/listingOptions";
import AmenityPicker from "@/components/AmenityPicker";
import {
  BottomSheet,
  BottomSheetContent,
  BottomSheetFooter,
  BottomSheetHeader,
  BottomSheetTitle,
  BottomSheetTrigger,
} from "@/components/ui/bottom-sheet";

/** Session flag: the renter turned the profile filters off for this visit. */
const PROFILE_OFF_KEY = "msth.browse.profile-off";

// The URL is the source of truth for every filter and the sort: the search is
// parsed from `useSearchParams()` on each render and every control writes
// back through one `update()` (replace, so Back leaves the page in one step).
// That is what makes a search shareable, bookmarkable and restorable, and it
// means no effect ever has to copy state into the URL. The grammar lives in
// src/lib/browseFilters.ts.

// Amenity chips come from the registry (facets first, then every amenity by
// category) and SERVICE_FILTERS from the shared listingOptions module, so
// the browse filters always match what listings can set.

const toggleIn = (list: readonly string[], value: string): string[] =>
  list.includes(value) ? list.filter((x) => x !== value) : [...list, value];

const BrowseProperties = () => {
  const tenant = useTenant();
  const copy = useCopyResolver();
  const absUrl = useAbsUrl();
  const LOCATION_OPTIONS = tenant.areas;
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const { toggleFavorite, isFavorite, authPromptOpen, setAuthPromptOpen } = useFavorites();
  const { data: properties = [], isLoading } = useProperties();
  const { user } = useAuth();
  const { weights } = useMatchConfig();
  const [renterPrefs, setRenterPrefs] = useState<RenterPreferences | null>(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [isRenter, setIsRenter] = useState(false);
  // "Show all homes" or "Only this search": the profile stops steering the
  // search for the rest of the visit.
  const [profileOff, setProfileOff] = useState(() => {
    try { return sessionStorage.getItem(PROFILE_OFF_KEY) === "1"; } catch { return false; }
  });
  const turnProfileOff = useCallback(() => {
    setProfileOff(true);
    try { sessionStorage.setItem(PROFILE_OFF_KEY, "1"); } catch { /* private mode */ }
  }, []);
  // Set once the renter changes a filter themselves (not a link, not the profile).
  const [touched, setTouched] = useState(false);

  // Load renter preferences so we can show match % badges + power the
  // "save these filters as my preferences" action. Re-usable so saving can
  // refresh afterwards.
  const loadRenterPrefs = useCallback(async () => {
    if (!user) { setRenterPrefs(null); return; }
    const { data } = await supabase
      .from("profiles")
      .select(
        "preferred_budget_min, preferred_budget_max, preferred_areas, preferred_types, preferred_bedrooms, preferred_bathrooms, preferred_amenities, preferred_services, move_in_date, move_out_date, num_people, has_pets, preferred_dealbreakers, user_types"
      )
      .eq("user_id", user.id)
      .maybeSingle();
    const types = (data?.user_types ?? []) as string[];
    setIsRenter(!types.length || types.includes("renter"));
    setRenterPrefs(data ? profileRowToPreferences(data) : null);
  }, [user]);

  useEffect(() => { loadRenterPrefs(); }, [loadRenterPrefs]);

  // Scores on this site read only this market's areas (Oct 17,
  // renterMarkets.ts); the stored preferences keep every market's.
  const matchPrefs = useMemo<RenterPreferences | null>(() => {
    if (!renterPrefs) return null;
    return preferencesForSite(renterPrefs, { id: tenant.id, areas: tenant.areas, areaSlugs: tenant.areaSlugs });
  }, [renterPrefs, tenant]);
  const showMatchBadges = !!(matchPrefs && hasAnyPreference(matchPrefs));

  // Dynamic price bounds, no upper cap. Round listings up to give headroom
  // above the most expensive listing so renters can include it in the range.
  const { priceMin, priceMax } = useMemo(() => priceBoundsOf(properties), [properties]);
  const ctx = useMemo<BrowseContext>(
    () => ({ areaNames: tenant.areas, areaSlugs: tenant.areaSlugs, priceMin, priceMax }),
    [tenant.areas, tenant.areaSlugs, priceMin, priceMax],
  );

  const money = useCallback((n: number) => formatMoney(n, tenantCurrency(tenant)), [tenant]);

  // The search as the URL says it is. Area labels arriving late (tenant
  // load) and price bounds arriving late (listings load) both re-parse on
  // the next render, so nothing here can go stale.
  const search = useMemo(() => parseBrowseSearch(searchParams, ctx), [searchParams, ctx]);
  // "Best Match" only exists for renters with saved preferences; a shared
  // ?sort=match link falls back to price-desc for everyone else.
  const sort = resolveSort(search.sort, showMatchBadges);

  // The one writer. Merges a patch into the URL's current search and only
  // rewrites the browse keys, so ?tenant= and utm_* survive. One call per
  // handler: the functional form reads the render's params, so two calls in
  // the same handler would overwrite each other.
  const update = useCallback(
    (patch: Partial<BrowseSearch>) => {
      if (!("sort" in patch && Object.keys(patch).length === 1)) setTouched(true);
      setSearchParams(
        (prev) => applyBrowseSearch(browseSearchToParams({ ...parseBrowseSearch(prev, ctx), ...patch }, ctx), prev),
        { replace: true },
      );
    },
    [setSearchParams, ctx],
  );

  // Slider position while dragging; null = show the URL's range.
  const [priceDraft, setPriceDraft] = useState<[number, number] | null>(null);
  const priceShown = priceDraft ?? search.priceRange;

  const promotions = usePromotions();
  const filtered = useMemo(() => {
    const score = sort === "match" && matchPrefs ? (p: (typeof properties)[number]) => scoreProperty(p, matchPrefs, weights) : undefined;
    return promoteProperties(sortProperties(filterProperties(properties, search, ctx), sort, score), promotions, Math.floor(Date.now() / 86_400_000));
  }, [properties, search, ctx, sort, matchPrefs, weights, promotions]);
  // The match badge per card, scored once per listing when the list or the
  // preferences change, not on every render of the grid.
  const matchScores = useMemo(() => {
    const m = new Map<string, number>();
    if (showMatchBadges && matchPrefs) for (const p of filtered) m.set(p.id, scoreProperty(p, matchPrefs, weights));
    return m;
  }, [filtered, showMatchBadges, matchPrefs, weights]);

  // One "search" event per settled filter change (debounced), with the
  // result count, the demand signal behind "what renters look for". Keyed
  // on the browse part of the URL: the page's first URL (empty or a shared
  // link) never fires, and neither do tenant-list or price-bound arrivals.
  const urlKey = pickBrowseParams(searchParams).toString();
  const lastTracked = useRef<string | null>(null);
  useEffect(() => {
    if (isLoading) return;
    if (lastTracked.current === null) {
      lastTracked.current = urlKey;
      return;
    }
    if (lastTracked.current === urlKey) return;
    const t = setTimeout(() => {
      lastTracked.current = urlKey;
      track("search", {
        results: filtered.length,
        areas: search.locations.slice(0, 5),
        beds: search.bedrooms,
        baths: search.bathrooms,
        type: search.type,
        amenities: search.selectedAmenities.length,
        services: search.selectedServices.length,
        // Which ones (keys, ≤10): the demand signal behind collections.
        amenity_keys: labelsToKeys(search.selectedAmenities).slice(0, 10),
        service_keys: search.selectedServices.map(foldKey).slice(0, 10),
        price_min: search.priceRange[0],
        price_max: search.priceRange[1],
        from: search.fromDate ? format(search.fromDate, "yyyy-MM-dd") : null,
        to: search.toDate ? format(search.toDate, "yyyy-MM-dd") : null,
      });
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKey, isLoading]);

  // A signed-in renter's search starts from their profile (Oct 13 check-in):
  // only homes free for their dates, in budget, and so on. A link with
  // filters wins; "Show all homes" turns it off for the visit.
  const profileApplied = useRef(false);
  useEffect(() => {
    if (!renterPrefs || !isRenter || profileOff || profileApplied.current) return;
    profileApplied.current = true;
    if (BROWSE_FILTER_KEYS.some((k) => searchParams.get(k))) return;
    const fromProfile = preferencesToBrowseParams(renterPrefs, ctx);
    if (!fromProfile.toString()) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(fromProfile);
      const sortParam = prev.get("sort");
      if (sortParam) next.set("sort", sortParam);
      return applyBrowseSearch(next, prev);
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renterPrefs, isRenter, profileOff]);

  // "Back to listings" on a property page restores this search.
  useEffect(() => {
    rememberBrowseSearch(searchParams);
  }, [searchParams]);

  // Area chips: the tenant's areas, plus any token from a shared link that
  // this tenant cannot name (still filters, so it must be visible/removable).
  const locationChips = useMemo(
    () => [...LOCATION_OPTIONS, ...search.locations.filter((l) => !LOCATION_OPTIONS.includes(l))],
    [LOCATION_OPTIONS, search.locations],
  );

  const today = new Date(new Date().setHours(0, 0, 0, 0));

  const filterPanel = (
    <div className="space-y-4">
      <div>
        <label className="text-[13px] font-medium mb-2 block">
          Location {search.locations.length > 0 && <span className="text-muted-foreground font-normal">({search.locations.length})</span>}
        </label>
        <div className="grid grid-cols-2 gap-1.5">
          {locationChips.map((l) => (
            <Chip
              key={l}
              label={l}
              checked={search.locations.includes(l)}
              onChange={() => update({ locations: toggleIn(search.locations, l) })}
            />
          ))}
        </div>
      </div>
      <div>
        <label className="text-[13px] font-medium mb-2 block">Property Type</label>
        <div className="grid grid-cols-2 gap-1.5">
          {TYPE_OPTIONS.map((o) => (
            <Chip
              key={o}
              label={typeLabel(o)}
              checked={search.type === o}
              onChange={() => update({ type: o === search.type ? "any" : o })}
            />
          ))}
        </div>
      </div>
      <div>
        <label className="text-[13px] font-medium mb-2 block">Your dates</label>
        <div className="space-y-1.5">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className={cn("w-full justify-start text-left font-normal rounded-lg h-9 text-[13px]", !search.fromDate && "text-muted-foreground")}>
                <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                {search.fromDate ? format(search.fromDate, "MMM d, yyyy") : "From"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={search.fromDate} onSelect={(d) => update({ fromDate: d })} disabled={(d) => d < today} initialFocus className={cn("p-3 pointer-events-auto")} />
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className={cn("w-full justify-start text-left font-normal rounded-lg h-9 text-[13px]", !search.toDate && "text-muted-foreground")}>
                <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                {search.toDate ? format(search.toDate, "MMM d, yyyy") : "To"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={search.toDate} onSelect={(d) => update({ toDate: d })} disabled={(d) => (search.fromDate ? d < search.fromDate : d < today)} initialFocus className={cn("p-3 pointer-events-auto")} />
            </PopoverContent>
          </Popover>
          {(search.fromDate || search.toDate) && (
            <button type="button" onClick={() => update({ fromDate: undefined, toDate: undefined })} className="text-[12px] text-muted-foreground hover:text-foreground underline">
              Clear dates
            </button>
          )}
        </div>
      </div>
      <div>
        <label className="text-[13px] font-medium mb-2 block">Monthly Rent</label>
        <Slider
          value={priceShown}
          min={priceMin}
          max={priceMax}
          step={100}
          onValueChange={(v) => setPriceDraft([v[0], v[1]])}
          onValueCommit={(v) => { setPriceDraft(null); update({ priceRange: [v[0], v[1]] }); }}
          className="mt-2"
        />
        <div className="flex justify-between text-[12px] text-muted-foreground mt-1.5"><span>${priceShown[0].toLocaleString()}</span><span>{priceShown[1] >= priceMax ? `$${priceShown[1].toLocaleString()}+` : `$${priceShown[1].toLocaleString()}`}</span></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[13px] font-medium mb-1.5 block">Min Bedrooms</label>
          <Select value={search.bedrooms} onValueChange={(v) => update({ bedrooms: v as BedroomOption })}>
            <SelectTrigger className="rounded-lg h-9 text-[13px]"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              {BEDROOM_OPTIONS.map((o) => (
                <SelectItem key={o} value={o}>{o === "any" ? "Any" : `${o}+`}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[13px] font-medium mb-1.5 block">Min Bathrooms</label>
          <Select value={search.bathrooms} onValueChange={(v) => update({ bathrooms: v as BathroomOption })}>
            <SelectTrigger className="rounded-lg h-9 text-[13px]"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              {BATHROOM_OPTIONS.map((o) => (
                <SelectItem key={o} value={o}>{o === "any" ? "Any" : `${o}+`}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <label className="text-[13px] font-medium mb-2 block">Amenities</label>
        <AmenityPicker
          selected={search.selectedAmenities}
          onToggle={(a) => update({ selectedAmenities: toggleIn(search.selectedAmenities, a) })}
        />
      </div>
      <div>
        <label className="text-[13px] font-medium mb-2 block">Services Included in Rent</label>
        <div className="grid grid-cols-2 gap-1.5">
          {SERVICE_FILTERS.map((s) => (
            <Chip
              key={s}
              label={s}
              checked={search.selectedServices.includes(s)}
              onChange={() => update({ selectedServices: toggleIn(search.selectedServices, s) })}
            />
          ))}
        </div>
      </div>
    </div>
  );

  const browseJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${tenant.siteUrl}/` },
        { "@type": "ListItem", position: 2, name: "Browse rentals", item: `${tenant.siteUrl}/properties` },
      ],
    },
    ...(filtered.length > 0 ? [{
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `Rentals available on ${tenant.siteName}`,
      numberOfItems: filtered.length,
      itemListElement: filtered.slice(0, 25).map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${tenant.siteUrl}/properties/${p.slug}`,
        name: p.title,
      })),
    }] : []),
  ];

  const activeFilterCount = countActiveFilters(search, ctx);

  // Drops every filter (not the sort) and anything the slider was mid-drag on.
  const clearAllFilters = () => {
    setPriceDraft(null);
    setTouched(true);
    setSearchParams((prev) => clearBrowseFilters(prev), { replace: true });
  };

  // Alert defaults follow a single selected area (as its slug) and the
  // bedroom minimum, like the filters themselves.
  const alertDefaults = {
    area: search.locations.length === 1 ? areaSlugFor(search.locations[0], ctx) : undefined,
    bedrooms: search.bedrooms !== "any" ? parseInt(search.bedrooms) : undefined,
    amenities: search.selectedAmenities,
  };

  // Persist the current (temporary) filters as the renter's permanent profile
  // preferences. Saves values only; prunes any deal-breakers whose item is no
  // longer selected. Deal-breakers themselves are managed on the Profile page.
  // Area tokens this tenant cannot name (from a foreign link) are not saved.
  const saveAsPreferences = async () => {
    if (!user) { setAuthPromptOpen(true); return; }
    setSavingPrefs(true);
    const payload = keepRankedTypes(browseFiltersToProfilePayload({
      ...search,
      locations: search.locations.filter((l) => areaSlugFor(l, ctx)),
      priceMin,
      priceMax,
    }), search, renterPrefs, ctx);
    // These areas were picked on this market's site: they replace this
    // market's areas and keep the renter's areas in other markets (Oct 17).
    payload.preferred_areas = replaceMarketAreas(renterPrefs?.preferredAreas, payload.preferred_areas,
      { id: tenant.id, areas: tenant.areas, areaSlugs: tenant.areaSlugs });
    const dealbreakers = pruneDealbreakers((renterPrefs?.dealbreakers ?? []) as string[], payload);
    const { error } = await supabase
      .from("profiles")
      .update({ ...payload, preferred_dealbreakers: dealbreakers })
      .eq("user_id", user.id);
    setSavingPrefs(false);
    if (error) { toast.error("Couldn't save preferences"); return; }
    toast.success("Saved as your preferences");
    setTouched(false);
    loadRenterPrefs();
  };

  // Share the search as a canonical link on the production domain: browse
  // keys only (no tenant override, no utm), in the grammar's fixed order.
  // Native share sheet where there is one, else the clipboard.
  const shareSearch = async () => {
    const qs = pickBrowseParams(searchParams).toString();
    const url = absUrl(`/properties${qs ? `?${qs}` : ""}`);
    const title = filtered.length
      ? `${filtered.length} ${filtered.length === 1 ? "home" : "homes"} on ${tenant.siteName}`
      : `Rentals on ${tenant.siteName}`;
    const copyLink = async () => {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    };
    let method: "native" | "copy" = "copy";
    try {
      if (typeof navigator.share === "function") {
        await navigator.share({ title, url });
        method = "native";
      } else {
        await copyLink();
      }
    } catch (e) {
      if ((e as { name?: string })?.name === "AbortError") return; // sheet dismissed
      try {
        await copyLink();
      } catch {
        toast.error("Couldn't copy the link");
        return;
      }
    }
    track("share_click", { target: "search", method, filters: activeFilterCount, results: filtered.length });
  };

  // The profile steers this search: a strip says so; a search the renter
  // changed away from it offers to update the profile (once it settles).
  const profileParams = renterPrefs && isRenter ? preferencesToBrowseParams(renterPrefs, ctx).toString() : "";
  const differs = !!renterPrefs && isRenter && searchDiffersFromProfile(search, renterPrefs, ctx);
  const showProfileStrip = !!user && !profileOff && !!profileParams && !differs && activeFilterCount > 0;
  const [settledKey, setSettledKey] = useState(urlKey);
  useEffect(() => {
    const t = setTimeout(() => setSettledKey(urlKey), 1200);
    return () => clearTimeout(t);
  }, [urlKey]);
  const askToUpdate = !!user && isRenter && !!renterPrefs && !profileOff && touched && differs && settledKey === urlKey;
  const profileBanner = askToUpdate ? (
    <div role="status" className="mb-4 rounded-card border border-primary/30 bg-terracotta-light px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-[14px]">
      <span className="min-w-0">Update your profile to these filters? Your match scores will follow.</span>
      <span className="flex items-center gap-2 shrink-0">
        <Button size="sm" className="rounded-full" onClick={saveAsPreferences} disabled={savingPrefs}>Update profile</Button>
        <Button size="sm" variant="ghost" className="rounded-full" onClick={turnProfileOff}>Only this search</Button>
      </span>
    </div>
  ) : showProfileStrip ? (
    <div className="mb-4 rounded-card border border-border bg-card px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-[14px]">
      <span className="flex items-center gap-2 min-w-0"><UserRound className="h-4 w-4 text-primary shrink-0" />Filtered by your profile</span>
      <span className="flex items-center gap-3 shrink-0 text-[13px]">
        <Link to="/profile" className="underline underline-offset-4 text-foreground">Edit profile</Link>
        <button type="button" className="underline underline-offset-4 text-muted-foreground hover:text-foreground" onClick={() => { turnProfileOff(); clearAllFilters(); setTouched(false); }}>Show all homes</button>
      </span>
    </div>
  ) : null;

  // Every active filter, removable in one tap, above the results on every
  // screen (Oct 15: a type from the profile emptied the page and the only
  // way out was a dropdown deep in the panel).
  const chips = activeFilterChips(search, ctx, money);
  const activeFilters = chips.length > 0 ? (
    <div role="group" aria-label="Active filters" className="mb-4 flex flex-wrap items-center gap-2">
      {chips.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={() => { setPriceDraft(null); update(c.patch); }}
          aria-label={`Remove ${c.label}`}
          className="inline-flex max-w-full items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-[13px] text-primary hover:bg-primary/15"
        >
          <span className="truncate">{c.label}</span>
          <X className="h-3.5 w-3.5 shrink-0" aria-hidden />
        </button>
      ))}
      <button type="button" onClick={clearAllFilters} className="text-[13px] text-muted-foreground underline underline-offset-4 hover:text-foreground">
        Clear all
      </button>
    </div>
  ) : null;

  // No results: the filters whose removal alone brings homes back.
  const noResults = !isLoading && filtered.length === 0;
  const hints = useMemo(
    () => (noResults ? relaxations(properties, search, ctx, money).slice(0, 3) : []),
    [noResults, properties, search, ctx, money],
  );

  const savePrefsButton = activeFilterCount > 0 ? (
    <Button
      variant="outline"
      className="w-full rounded-full gap-2"
      onClick={saveAsPreferences}
      disabled={savingPrefs}
    >
      <BookmarkPlus className="h-4 w-4" />
      {user ? "Save as my preferences" : "Sign in to save as preferences"}
    </Button>
  ) : null;

  return (
    <div>
      <Seo
        title={copy(seoTitleKey("properties")).value}
        description={copy(seoDescriptionKey("properties")).value}
        path="/properties"
        social="properties"
        jsonLd={browseJsonLd}
      />
      <PageContainer variant="bleed">
        {/* Collapsible email-alert card, same on mobile and desktop. */}
        <div className="mb-6">
          <details className="[&[open]_.alert-chev]:rotate-180">
            <summary className="list-none cursor-pointer flex items-center justify-between gap-3 rounded-card border border-border bg-card px-4 py-3 shadow-soft">
              <span className="flex items-center gap-2 text-[14px] font-medium text-foreground">
                <MailPlus className="h-4 w-4 text-primary" />
                Get email alerts for new listings
              </span>
              <ChevronDown className="alert-chev h-4 w-4 text-muted-foreground transition-transform" />
            </summary>
            <div className="mt-2">
              <AlertSignup
                variant="properties-top"
                source="browse_top"
                promptSignupAfter
                defaults={alertDefaults}
              />
            </div>
          </details>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6 mt-8">
          <div className="min-w-0">
            <h1 className="font-serif text-2xl sm:text-[32px] font-medium leading-tight">Browse Rentals</h1>
            {/* Without dates nothing is filtered by availability, so don't say
                "available" (Angelina Oct 23, Felipe approved Oct 25). */}
            <p className="text-muted-foreground text-[14px] mt-1" data-testid="browse-count">
              {isLoading
                ? "Loading..."
                : search.fromDate || search.toDate
                  ? `${filtered.length} ${filtered.length === 1 ? "home" : "homes"} for your dates`
                  : `${filtered.length} ${filtered.length === 1 ? "home" : "homes"}. Pick your dates to see which are free then.`}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="icon"
              className="rounded-full h-10 w-10 shrink-0"
              onClick={shareSearch}
              aria-label="Share this search"
              title="Share this search"
            >
              <Share2 className="h-4 w-4" />
            </Button>
            <Select value={sort} onValueChange={(v) => update({ sort: v as BrowseSort })}>
              <SelectTrigger className="w-28 sm:w-40 rounded-full h-10 text-[13px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {showMatchBadges && <SelectItem value="match">Best Match</SelectItem>}
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="price-asc">Price ↑</SelectItem>
                <SelectItem value="price-desc">Price ↓</SelectItem>
              </SelectContent>
            </Select>
            {/* Mobile-only Filters trigger. Desktop has an always-visible
                sidebar instead, so no need for a toggle there. */}
            <BottomSheet open={showFilters} onOpenChange={setShowFilters}>
              <BottomSheetTrigger asChild>
                <Button variant="outline" className="md:hidden rounded-full h-10 gap-1.5 text-[13px]">
                  <SlidersHorizontal className="h-3.5 w-3.5" />
                  Filters
                  {activeFilterCount > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-semibold">
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
              </BottomSheetTrigger>
              <BottomSheetContent className="flex flex-col p-0 w-full">
                <BottomSheetHeader className="px-5 pt-5 pb-3 border-b">
                  <div className="flex items-center justify-between">
                    <BottomSheetTitle>Filters</BottomSheetTitle>
                    {activeFilterCount > 0 && (
                      <button
                        type="button"
                        onClick={clearAllFilters}
                        className="text-[13px] text-muted-foreground hover:text-foreground underline underline-offset-4"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                </BottomSheetHeader>
                <div className="flex-1 overflow-y-auto px-5 py-4">
                  {filterPanel}
                </div>
                <BottomSheetFooter className="px-5 py-3">
                  <div className="w-full flex flex-col gap-2">
                    {savePrefsButton}
                    <Button
                      className="w-full rounded-full h-12"
                      size="lg"
                      onClick={() => setShowFilters(false)}
                    >
                      Show {filtered.length} {filtered.length === 1 ? "result" : "results"}
                    </Button>
                  </div>
                </BottomSheetFooter>
              </BottomSheetContent>
            </BottomSheet>
          </div>
        </div>

        {/* Layout: single column on mobile; 2-column with sticky filter
            sidebar on desktop. The sidebar uses the --app-nav-height
            system so it sits flush below the chrome and scrolls
            independently when taller than the viewport. */}
        <div className="md:grid md:grid-cols-[18rem_1fr] lg:grid-cols-[20rem_1fr] md:gap-8">
          <aside className="hidden md:block">
            <div className="sticky top-nav max-h-[calc(100dvh-var(--app-nav-height))] overflow-y-auto py-1 pr-2 -mr-2">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-medium">Filters</h2>
                {activeFilterCount > 0 && (
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="text-[12px] text-muted-foreground hover:text-foreground underline underline-offset-4"
                  >
                    Clear all
                  </button>
                )}
              </div>
              {filterPanel}
              {savePrefsButton && <div className="mt-4">{savePrefsButton}</div>}
            </div>
          </aside>

          <div className="w-full min-w-0">
          {profileBanner}
          {activeFilters}
          {isLoading ? (
            // Skeleton grid, keeps the user from seeing the "Nothing fits
            // today" empty state flash during the React-Query transition when
            // navigating back from a property detail page.
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-[3/2] rounded-card bg-muted/50 animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="space-y-4">
              {hints.length > 0 && (
                <div role="status" className="rounded-card border border-border bg-card px-4 py-3">
                  <p className="text-[14px] font-medium">No homes match all these filters.</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {hints.map(({ chip, count }) => (
                      <Button key={chip.id} size="sm" variant="outline" className="rounded-full" onClick={() => { setPriceDraft(null); update(chip.patch); }}>
                        Remove {chip.label}: {count} {count === 1 ? "home" : "homes"}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              <AlertSignup
                variant="properties-empty"
                source="browse_empty"
                promptSignupAfter
                defaults={alertDefaults}
              />
            </div>
          ) : (
            // Full-bleed page (variant="bleed"); cap the grid at 3 cards/row so
            // each card/photo stays large on wide screens.
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {filtered.map((p, i) => (
                <PropertyCard
                  key={p.id}
                  property={p}
                  // Eager-load the first row (LCP) on the search grid; the rest
                  // stay lazy. This is the page's primary content near the top.
                  priority={i < 3}
                  isFavorite={isFavorite(p.id)}
                  onToggleFavorite={toggleFavorite}
                  matchScore={matchScores.get(p.id)}
                />
              ))}
            </div>
          )}
          </div>
        </div>
      </PageContainer>
      <AuthPromptDialog open={authPromptOpen} onOpenChange={setAuthPromptOpen} />
    </div>
  );
};

export default BrowseProperties;
